/** Établissements (section 4) : liste (recherche, filtre segmenté, tri par en-tête) + fiche
 * pleine page (identité, slug auto-déduit, coordonnées, utilisateurs, export .cavorama, zone sensible). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../../services/api';
import { Button, SegmentedControl } from '../../../components/ui/kit';
import { useSortableTable, SortableTh } from '../useSortableTable';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { FullPanel, PanelCard, Field } from '../FullPanel';
import { TENANT_TYPE_LABELS, ROLE_LABELS } from '../constants';
import { timeAgo, dateOnly } from '../format';

function slugify(s) {
  return (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function TenantPanel({ tenant, users, onBack, onSaved, reload }) {
  const isNew = !tenant.id;
  const [form, setForm] = useState({
    name: tenant.name || '', type: tenant.type || 'pro', slug: tenant.slug || '',
    email: tenant.contact?.email || '', phone: tenant.contact?.phone || '',
    address: tenant.contact?.address || '', postal_code: tenant.contact?.postal_code || '',
    city: tenant.contact?.city || '', manager_name: '', manager_email: '',
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [dirty, setDirty] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const effectiveSlug = form.slug || slugify(form.name);
  const tenantUsers = users.filter((u) => u.tenant?.id === tenant.id);

  // Enregistrement AUTOMATIQUE (fiche existante), comme côté établissement.
  const autoPayload = { name: form.name, type: form.type, slug: form.slug,
    email: form.email, phone: form.phone, address: form.address, postal_code: form.postal_code, city: form.city };
  const autoStatus = useAutoSave(isNew ? {} : autoPayload, async (v) => {
    if (isNew) return;
    await adminApi.updateTenant(tenant.id, v); onSaved();
  });

  const save = async () => {
    try {
      const payload = {
        name: form.name, type: form.type, slug: form.slug || slugify(form.name),
        email: form.email, phone: form.phone, address: form.address,
        postal_code: form.postal_code, city: form.city,
      };
      if (form.manager_email) payload.manager = { name: form.manager_name, email: form.manager_email };
      await adminApi.createTenant(payload);
      toast.success('Établissement créé');
      setDirty(false); onSaved(); onBack();
    } catch (e) { toast.error(e.message || 'Création impossible'); }
  };
  const back = () => { if (isNew && dirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter ?')) return; onBack(); };
  const toggleActive = async () => {
    try {
      if (tenant.active) await adminApi.disableTenant(tenant.id); else await adminApi.enableTenant(tenant.id);
      toast.success(tenant.active ? 'Établissement désactivé' : 'Établissement réactivé'); reload();
    } catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    if (!window.confirm(`Supprimer « ${tenant.name} » et TOUTES ses données (${tenantUsers.length} compte·s) ? Action irréversible.`)) return;
    try { await adminApi.deleteTenant(tenant.id); toast.success('Établissement supprimé'); onBack(); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  const doExport = () => adminApi.exportTenant(tenant.id, `${effectiveSlug}-${new Date().toISOString().slice(0, 10)}.cavorama`)
    .then(() => toast.success('Archive téléchargée')).catch((e) => toast.error(e.message));

  return (
    <FullPanel testId="tenant-panel" title={isNew ? 'Nouvel établissement' : tenant.name}
      subtitle={isNew ? null : TENANT_TYPE_LABELS[tenant.type]}
      onBack={back} dirty={dirty} onCancel={back} onSave={isNew ? save : undefined}
      autoSaveStatus={isNew ? undefined : autoStatus}
      saveLabel="Créer l'établissement">
      <div className="adm-panel-grid">
        <PanelCard title="Identité" testId="tenant-identity">
          <Field label="Nom"><input className="vd-input" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="tenant-name" /></Field>
          <Field label="Lien public (slug)" hint={`Carte publique : cavorama.com/${effectiveSlug || '…'}`}>
            <input className="vd-input adm-mono" value={form.slug} placeholder={slugify(form.name)}
              onChange={(e) => { setSlugTouched(true); set('slug', e.target.value); }}
              onBlur={(e) => set('slug', slugify(e.target.value))} data-testid="tenant-slug" />
          </Field>
          <Field label="Type">
            <SegmentedControl testId="tenant-type" value={form.type} onChange={(v) => set('type', v)}
              options={[{ value: 'pro', label: 'Professionnel' }, { value: 'particulier', label: 'Particulier' }]} />
          </Field>
        </PanelCard>

        <PanelCard title="Coordonnées" testId="tenant-contact">
          <Field label="Email de contact"><input className="vd-input" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="Téléphone"><input className="vd-input" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label="Adresse"><input className="vd-input" value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Code postal"><input className="vd-input" value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} /></Field>
            <Field label="Ville"><input className="vd-input" value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
          </div>
        </PanelCard>

        {isNew ? (
          <PanelCard title="Premier gérant (optionnel)" testId="tenant-manager">
            <p className="text-xs text-n600 mb-3">Un compte Gérant sera créé et recevra l'email d'accès.</p>
            <Field label="Nom"><input className="vd-input" value={form.manager_name} onChange={(e) => set('manager_name', e.target.value)} /></Field>
            <Field label="Email"><input className="vd-input" type="email" value={form.manager_email} onChange={(e) => set('manager_email', e.target.value)} data-testid="tenant-manager-email" /></Field>
          </PanelCard>
        ) : (
          <>
            <PanelCard title={`Utilisateurs · ${tenantUsers.length}`} testId="tenant-users">
              <ul className="space-y-1 mb-3">
                {tenantUsers.map((u) => (
                  <li key={u.id} className="text-sm flex justify-between items-center cursor-pointer hover:bg-surface/50 rounded px-2 py-1"
                    onClick={() => navigate(`/admin/users?open=${u.id}`)}>
                    <span>{u.name} <span className="text-n600">· {ROLE_LABELS[u.role] || u.role}</span></span>
                    <span className="adm-badge">{u.active ? 'actif' : 'bloqué'}</span>
                  </li>
                ))}
                {!tenantUsers.length && <li className="text-sm text-n500">Aucun utilisateur.</li>}
              </ul>
              <button className="vd-link text-sm" onClick={() => navigate(`/admin/users?new=${tenant.id}`)} data-testid="tenant-add-user">Ajouter un utilisateur</button>
            </PanelCard>

            <PanelCard title="Informations" testId="tenant-info">
              <p className="text-sm mb-1">Identifiant : <span className="adm-mono text-xs">{tenant.id}</span></p>
              <p className="text-sm mb-1">Créé le : {dateOnly(tenant.created_at)}</p>
              <p className="text-sm mb-1">Dernière activité : {timeAgo(tenant.last_activity_at)}</p>
              <p className="text-sm mb-3">État : <span className={`adm-badge ${tenant.active ? 'adm-badge-active' : ''}`}>{tenant.active ? 'actif' : 'désactivé'}</span></p>
              <Button onClick={doExport} data-testid="tenant-export">Exporter l'établissement</Button>
            </PanelCard>

            <PanelCard title="Zone sensible" testId="tenant-danger" className="border-red-700/40">
              <p className="text-xs text-n600 mb-3">La désactivation bloque tous les accès sans effacer les données.</p>
              <div className="flex gap-2">
                <Button onClick={toggleActive} data-testid="tenant-toggle">{tenant.active ? 'Désactiver' : 'Réactiver'}</Button>
                <Button onClick={del} className="text-red-700 border-red-700/50" data-testid="tenant-delete">Supprimer</Button>
              </div>
            </PanelCard>
          </>
        )}
      </div>
    </FullPanel>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [seg, setSeg] = useState('all');
  const [open, setOpen] = useState(null); // tenant object or {} for new
  const [params, setParams] = useSearchParams();

  const load = () => Promise.all([adminApi.tenants(), adminApi.users()]).then(([t, u]) => { setTenants(t); setUsers(u); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = params.get('open');
    if (id && tenants.length) { const t = tenants.find((x) => x.id === id); if (t) setOpen(t); }
  }, [params, tenants]);

  const filtered = useMemo(() => tenants.filter((t) => {
    const matchQ = !q || `${t.name} ${t.slug}`.toLowerCase().includes(q.toLowerCase());
    const matchSeg = seg === 'all' || (seg === 'active' && t.active) || (seg === 'disabled' && !t.active);
    return matchQ && matchSeg;
  }), [tenants, q, seg]);

  const getters = {
    name: (t) => t.name, type: (t) => t.type, slug: (t) => t.slug,
    users: (t) => t.user_count, activity: (t) => t.last_activity_at || '', state: (t) => (t.active ? 1 : 0),
  };
  const { sorted, sort, toggle } = useSortableTable(filtered, getters, { key: 'name', dir: 'asc' });

  const closePanel = () => { setOpen(null); if (params.get('open')) { params.delete('open'); setParams(params, { replace: true }); } };

  return (
    <div data-testid="admin-tenants" className="relative min-h-[70vh]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-title text-3xl">Établissements</h1>
        <Button variant="primary" onClick={() => setOpen({})} data-testid="new-tenant">Nouvel établissement</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className="vd-input" style={{ width: 'auto', flex: '0 1 260px' }} placeholder="Rechercher (nom ou slug)"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-search" />
        <SegmentedControl value={seg} onChange={setSeg}
          options={[{ value: 'all', label: 'Tous' }, { value: 'active', label: 'Actifs' }, { value: 'disabled', label: 'Désactivés' }]} />
        <span className="text-xs text-n600 ml-auto">{sorted.length} résultat·s</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm adm-table">
          <thead><tr className="text-left text-n600 border-b border-hair">
            <SortableTh label="Nom" sortKey="name" sort={sort} toggle={toggle} />
            <SortableTh label="Type" sortKey="type" sort={sort} toggle={toggle} />
            <SortableTh label="Slug" sortKey="slug" sort={sort} toggle={toggle} />
            <SortableTh label="Utilisateurs" sortKey="users" sort={sort} toggle={toggle} />
            <SortableTh label="Dernière activité" sortKey="activity" sort={sort} toggle={toggle} />
            <SortableTh label="État" sortKey="state" sort={sort} toggle={toggle} />
            <th></th>
          </tr></thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id} className="border-b border-hair/60 hover:bg-surface/40" data-testid={`tenant-row-${t.slug}`}>
                <td className="py-2">
                  <button className="vd-link font-medium" onClick={() => setOpen(t)}>{t.name}</button>
                  <div className="text-xs text-n500">Créé le {dateOnly(t.created_at)}</div>
                </td>
                <td>{TENANT_TYPE_LABELS[t.type] || t.type}</td>
                <td className="adm-mono text-xs">{t.slug}</td>
                <td><button className="vd-link" onClick={() => window.location.assign(`/admin/users?tenant=${t.id}`)}>{t.user_count}</button></td>
                <td className="text-n600">{timeAgo(t.last_activity_at)}</td>
                <td><span className={`adm-badge ${t.active ? 'adm-badge-active' : ''}`}>{t.active ? 'actif' : 'désactivé'}</span></td>
                <td className="text-right whitespace-nowrap">
                  <button className="vd-link mr-3" onClick={() => setOpen(t)}>Modifier</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <TenantPanel tenant={open} users={users} onBack={closePanel} onSaved={load} reload={load} />}
    </div>
  );
}
